// Daemon Inspector — operator-only page that surfaces every daemon
// signal in one place: the latest cycle per kind (from the health
// snapshot), the recent cycle history (from /r/admin/daemon-cycles),
// and explicit "run now" + "retention cleanup" trigger buttons.
//
// Plan-admin/06: read-only is VIEWER, the run buttons require
// OPERATOR (and are rate-limited 1/min per operator on the server).

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DaemonCycle, DaemonCyclesResponse,
  DaemonKind, DaemonRunResponse, DaemonRunResultData, DaemonRunSummary,
  HealthResponse,
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { CodeChip } from '../components/CodeChip'
import { ConfirmActionDialog } from '../components/ConfirmActionDialog'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { useAdminMutation } from '../hooks/useAdminMutation'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

const KINDS: DaemonKind[] = ['CONFIRMATION', 'DELIVERY', 'RETENTION_CLEANUP']

export function DaemonInspectorPage() {
  const auth = useAuth()
  const canOperate = auth.requireRole('OPERATOR')
  const client = useAdminApiClient()

  const healthQuery = useQuery({
    queryKey: queryKeys.health(),
    queryFn: () => client.get<HealthResponse>('/r/admin/health'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  // review_comments M3 (HEAD db4990e): plan-admin/06 calls for a
  // sparkline-ish history table per kind. We render three sibling
  // tables instead of one interleaved table so an operator can scope
  // pagination and skim the cadence of each kind independently. Each
  // tab owns its own cursor query keyed on `kind`.
  const [activeKind, setActiveKind] = useState<DaemonKind>('CONFIRMATION')

  const runs: Partial<Record<DaemonKind, DaemonRunSummary>> =
    healthQuery.data?.success
      ? healthQuery.data.data.last_daemon_runs
      : {}

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            Daemon Inspector
          </h1>
          <p className="mt-1 text-sm text-stone-600">
            Latest cycle per kind, recent run history, and on-demand
            triggers. Triggers are audited and rate-limited to one
            invocation per minute per operator.
          </p>
        </div>
        {canOperate ? (
          <DaemonTriggers />
        ) : (
          <p
            className="text-xs text-stone-500"
            data-testid="viewer-notice"
          >
            Operator role required to trigger cycles.
          </p>
        )}
      </header>

      <section
        aria-label="Last run"
        className="grid grid-cols-1 gap-3 md:grid-cols-3"
      >
        {KINDS.map((kind) => (
          <LastRunCard
            key={kind}
            kind={kind}
            run={runs[kind]}
            isLoading={healthQuery.isLoading}
          />
        ))}
      </section>

      <section aria-label="Recent cycles" className="space-y-2">
        <div
          role="tablist"
          aria-label="Cycle kind"
          className="flex flex-wrap gap-1 border-b border-stone-200"
        >
          {KINDS.map((kind) => {
            const selected = kind === activeKind
            return (
              <button
                key={kind}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls={`daemon-cycles-${kind}`}
                id={`daemon-cycles-tab-${kind}`}
                onClick={() => setActiveKind(kind)}
                className={[
                  'rounded-t-md px-3 py-1.5 text-xs font-medium',
                  selected
                    ? 'border border-b-white border-stone-200 bg-white text-stone-900'
                    : 'text-stone-500 hover:text-stone-800',
                ].join(' ')}
              >
                {KIND_LABELS[kind]} cycles
              </button>
            )
          })}
        </div>
        {KINDS.map((kind) => (
          <DaemonCyclesTable
            key={kind}
            kind={kind}
            visible={kind === activeKind}
          />
        ))}
      </section>
    </div>
  )
}

const KIND_LABELS: Record<DaemonKind, string> = {
  CONFIRMATION: 'Confirmation',
  DELIVERY: 'Delivery',
  RETENTION_CLEANUP: 'Retention cleanup',
}

interface DaemonCyclesTableProps {
  kind: DaemonKind
  visible: boolean
}

function DaemonCyclesTable({ kind, visible }: DaemonCyclesTableProps) {
  const client = useAdminApiClient()
  const cyclesQuery = useCursorPaginatedQuery<DaemonCycle>({
    queryKey: queryKeys.daemonCycles({ limit: 50, kind }),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: '50', kind })
      if (cursor) search.set('cursor', cursor)
      return client.get<DaemonCyclesResponse>(
        `/r/admin/daemon-cycles?${search.toString()}`,
      )
    },
  })
  return (
    <div
      role="tabpanel"
      id={`daemon-cycles-${kind}`}
      aria-labelledby={`daemon-cycles-tab-${kind}`}
      hidden={!visible}
      data-testid={`daemon-cycles-panel-${kind}`}
    >
      <DataTable<DaemonCycle>
        caption={`Recent ${KIND_LABELS[kind].toLowerCase()} cycles`}
        columns={CYCLE_COLUMNS}
        rows={cyclesQuery.flatItems}
        rowKey={(row) => row.cycle_id}
        isLoading={cyclesQuery.isLoading}
        isError={cyclesQuery.isError}
        emptyTitle={`No ${KIND_LABELS[kind].toLowerCase()} cycles recorded yet`}
        emptyDescription="Cycles appear here as the daemon runs."
        footer={
          <PaginationControl
            loadedCount={cyclesQuery.flatItems.length}
            hasMore={cyclesQuery.hasMore}
            isFetchingMore={cyclesQuery.isFetchingMore}
            onLoadMore={() => cyclesQuery.loadMore()}
          />
        }
      />
    </div>
  )
}

interface LastRunCardProps {
  kind: DaemonKind
  run: DaemonRunSummary | undefined
  isLoading: boolean
}

function LastRunCard({ kind, run, isLoading }: LastRunCardProps) {
  return (
    <article
      className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      data-testid={`last-run-${kind}`}
    >
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-900">{kind}</h3>
        {run && (
          <span
            className={[
              'rounded-full px-2 py-0.5 text-xs font-medium',
              run.success
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-red-50 text-red-700',
            ].join(' ')}
          >
            {run.success ? 'success' : 'failure'}
          </span>
        )}
      </header>
      {isLoading && (
        <p className="mt-3 text-xs text-stone-500">Loading…</p>
      )}
      {!isLoading && !run && (
        <p className="mt-3 text-xs text-stone-500">
          No cycle recorded yet.
        </p>
      )}
      {run && (
        <dl className="mt-3 space-y-1 text-xs text-stone-700">
          <div className="flex justify-between">
            <dt className="text-stone-500">Started</dt>
            <dd>
              <RelativeTime timestamp={run.started_time} />
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-stone-500">Duration</dt>
            <dd>{run.duration_ms} ms</dd>
          </div>
          {run.error_message && (
            <div
              className="mt-2 rounded-md border border-red-200 bg-red-50
                         px-2 py-1 text-xs text-red-800"
            >
              {run.error_message}
            </div>
          )}
          <CountersList counters={run.counters} />
        </dl>
      )}
    </article>
  )
}

function CountersList({ counters }: { counters: Record<string, number> }) {
  const entries = Object.entries(counters)
  if (entries.length === 0) return null
  return (
    <div className="mt-2 border-t border-stone-100 pt-2">
      <ul className="space-y-0.5">
        {entries.map(([k, v]) => (
          <li key={k} className="flex justify-between">
            <span className="text-stone-500">{k}</span>
            <span className="font-mono">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const CYCLE_COLUMNS: ColumnConfig<DaemonCycle>[] = [
  {
    key: 'cycle_id', header: 'Cycle id',
    cell: (row) => <CodeChip>{row.cycle_id}</CodeChip>,
  },
  { key: 'kind', header: 'Kind', cell: (row) => row.kind },
  {
    key: 'started', header: 'Started',
    cell: (row) => <RelativeTime timestamp={row.started_time} />,
  },
  {
    key: 'duration', header: 'Duration',
    cell: (row) => `${row.duration_ms} ms`,
  },
  {
    key: 'success', header: 'Result',
    cell: (row) => row.success ? 'success' : 'failure',
  },
  {
    key: 'counters', header: 'Counters',
    cell: (row) => Object.entries(row.counters).map(
      ([k, v]) => `${k}=${v}`).join(', ') || '—',
  },
]

// review_comments L1 (HEAD db4990e): plan-admin/06 says rate-limit
// failures should read "Already triggered within the last minute."
// Backend continues to ship the raw `RATE_LIMITED` code (used by tests
// + logs) — the dialog maps it to operator-facing copy on render.
const RATE_LIMITED_COPY = 'Already triggered within the last minute.'

const DAEMON_TRIGGER_ERROR_COPY: Record<string, string> = {
  RATE_LIMITED: RATE_LIMITED_COPY,
  DAEMON_REASON_REQUIRED:
    'Reason is required (4-500 characters).',
}

const RETENTION_TRIGGER_ERROR_COPY: Record<string, string> = {
  RATE_LIMITED: RATE_LIMITED_COPY,
  RETENTION_CLEANUP_REASON_REQUIRED:
    'Reason is required (4-500 characters).',
}

function DaemonTriggers() {
  const [openDialog, setOpenDialog] = useState<
    null | 'run' | 'retention'
  >(null)
  const [lastResult, setLastResult] = useState<
    null | { kind: 'run' | 'retention'; data: DaemonRunResultData }
  >(null)

  // A "run cycle now" call writes both a CONFIRMATION and a DELIVERY
  // cycle row, so we invalidate the whole `daemonCycles` prefix to
  // refresh every per-kind tab and any future kind variants. The
  // health snapshot also moves under each trigger.
  const runMutation = useAdminMutation<{ reason: string }, DaemonRunResultData>({
    mutate: (client, { reason }) =>
      client.post<DaemonRunResponse>('/r/_daemon/run', { reason }),
    invalidates: [queryKeys.health(), ['daemonCycles']],
  })

  const retentionMutation = useAdminMutation<{ reason: string }, DaemonRunResultData>({
    mutate: (client, { reason }) =>
      client.post<DaemonRunResponse>(
        '/r/_daemon/retention_cleanup', { reason }),
    invalidates: [queryKeys.health(), ['daemonCycles']],
  })

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => setOpenDialog('run')}
        className="rounded-md border border-accent bg-white px-3 py-1
                   text-xs font-medium text-accent shadow-sm hover:bg-accent/5"
      >
        Run cycle now
      </button>
      <button
        type="button"
        onClick={() => setOpenDialog('retention')}
        className="rounded-md border border-stone-300 bg-white px-3 py-1
                   text-xs font-medium text-stone-700 shadow-sm hover:bg-stone-50"
      >
        Run retention cleanup now
      </button>

      {lastResult && (
        <details
          className="ml-2 max-w-md text-xs text-stone-700"
          data-testid="last-trigger-result"
        >
          <summary className="cursor-pointer text-stone-500">
            Last trigger result ({lastResult.kind})
          </summary>
          <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-stone-50
                          p-2 text-[11px] leading-snug text-stone-700">
            {JSON.stringify(lastResult.data, null, 2)}
          </pre>
        </details>
      )}

      {openDialog === 'run' && (
        <ConfirmActionDialog
          title="Run daemon cycle"
          body={
            <span>
              Triggers a confirmation + delivery cycle right now. The
              audit log row records the operator id and reason.
            </span>
          }
          confirmLabel="Run"
          errorMessages={DAEMON_TRIGGER_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const data = await runMutation.mutateAsync({ reason })
            setLastResult({ kind: 'run', data })
            setOpenDialog(null)
          }}
          onCancel={() => setOpenDialog(null)}
        />
      )}
      {openDialog === 'retention' && (
        <ConfirmActionDialog
          title="Run retention cleanup"
          body={
            <span>
              Runs the retention cleanup job to GC expired delivery
              records and old messages. Does not send any new emails.
            </span>
          }
          confirmLabel="Run"
          errorMessages={RETENTION_TRIGGER_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const data = await retentionMutation.mutateAsync({ reason })
            setLastResult({ kind: 'retention', data })
            setOpenDialog(null)
          }}
          onCancel={() => setOpenDialog(null)}
        />
      )}
    </div>
  )
}
